import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FlaskConical, Loader2, PenLine, X } from "lucide-react";
import type { ArticleIdeaDTO, DraftDTO } from "@app/shared";
import { api } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { formatDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { StatusBadge } from "../components/StatusBadge";
import { useToast } from "../components/Toast";

const IDEA_STATUS_VARIANT: Record<
  ArticleIdeaDTO["status"],
  "secondary" | "teal" | "green"
> = {
  suggested: "teal",
  used: "green",
  dismissed: "secondary",
};

/**
 * Artikel mode: research article topics, turn an idea into a written draft, and
 * manage the resulting article drafts (link through to ArticleDetail for editing
 * + WordPress publishing).
 */
export function Articles() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();

  const ideasQuery = useQuery({
    queryKey: ["articleIdeas", accountKey],
    queryFn: ({ signal }) =>
      api.articleIdeas({ accountKey: accountKey! }, signal),
    enabled: !!accountKey,
  });

  const articlesQuery = useQuery({
    queryKey: ["articles", accountKey],
    queryFn: ({ signal }) => api.articles({ accountKey: accountKey! }, signal),
    enabled: !!accountKey,
  });

  const research = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.researchArticles(accountKey);
    },
    onSuccess: () => {
      toast.success("Topic research requested — check back shortly");
      void queryClient.invalidateQueries({ queryKey: ["articleIdeas"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Research failed"),
  });

  const write = useMutation({
    mutationFn: (ideaId: string) => api.writeArticle(ideaId),
    onSuccess: () => {
      toast.success("Article write requested — a draft will appear below");
      void queryClient.invalidateQueries({ queryKey: ["articleIdeas"] });
      void queryClient.invalidateQueries({ queryKey: ["articles"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Write failed"),
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => api.dismissIdea(id),
    onSuccess: () => {
      toast.success("Idea dismissed");
      void queryClient.invalidateQueries({ queryKey: ["articleIdeas"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Dismiss failed"),
  });

  const ideas = ideasQuery.data ?? [];
  const articles = articlesQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy">Artikel</h1>
          <p className="text-sm text-slate-500">
            Research topics, write long-form drafts, and publish to WordPress.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Button
            variant="primary"
            onClick={() => research.mutate()}
            disabled={!accountKey || research.isPending}
          >
            {research.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FlaskConical className="h-4 w-4" />
            )}
            Riset topik
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ide topik</CardTitle>
          <CardDescription>
            Proposed article topics. &ldquo;Tulis&rdquo; writes a full draft;
            &ldquo;Abaikan&rdquo; dismisses the idea.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ideasQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-teal" />
            </div>
          ) : ideasQuery.isError ? (
            <p className="text-sm text-coral">Failed to load ideas.</p>
          ) : ideas.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No ideas yet. Use &ldquo;Riset topik&rdquo; to propose topics.
            </p>
          ) : (
            ideas.map((idea) => (
              <IdeaRow
                key={idea.id}
                idea={idea}
                onWrite={() => write.mutate(idea.id)}
                onDismiss={() => dismiss.mutate(idea.id)}
                writing={write.isPending && write.variables === idea.id}
                dismissing={dismiss.isPending && dismiss.variables === idea.id}
                busy={write.isPending || dismiss.isPending}
              />
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Draft artikel</CardTitle>
          <CardDescription>
            Written drafts. Open one to edit and publish to WordPress.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {articlesQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-teal" />
            </div>
          ) : articlesQuery.isError ? (
            <p className="text-sm text-coral">Failed to load article drafts.</p>
          ) : articles.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No article drafts yet. Write one from an idea above.
            </p>
          ) : (
            articles.map((article) => (
              <ArticleRow key={article.id} article={article} />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function IdeaRow({
  idea,
  onWrite,
  onDismiss,
  writing,
  dismissing,
  busy,
}: {
  idea: ArticleIdeaDTO;
  onWrite: () => void;
  onDismiss: () => void;
  writing: boolean;
  dismissing: boolean;
  busy: boolean;
}) {
  const actionable = idea.status === "suggested";
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={IDEA_STATUS_VARIANT[idea.status]}>
            {idea.status}
          </Badge>
          {idea.angle ? (
            <span className="text-xs text-slate-500">{idea.angle}</span>
          ) : null}
        </div>
        <p className="font-medium text-slate-900">{idea.title}</p>
        {idea.rationale ? (
          <p className="text-sm text-slate-600">{idea.rationale}</p>
        ) : null}
        {idea.keywords.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {idea.keywords.map((kw) => (
              <Badge key={kw} variant="secondary">
                {kw}
              </Badge>
            ))}
          </div>
        ) : null}
        {idea.status === "used" && idea.draftId ? (
          <Link
            to={`/articles/${idea.draftId}`}
            className="inline-flex text-xs text-teal hover:underline"
          >
            View written draft
          </Link>
        ) : null}
      </div>
      {actionable ? (
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={onWrite}
            disabled={busy}
          >
            {writing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PenLine className="h-4 w-4" />
            )}
            Tulis
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDismiss}
            disabled={busy}
          >
            {dismissing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
            Abaikan
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ArticleRow({ article }: { article: DraftDTO }) {
  return (
    <Link
      to={`/articles/${article.id}`}
      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-4 transition-colors hover:bg-slate-50"
    >
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate font-medium text-slate-900">
          {article.headline ?? "(no title)"}
        </p>
        <p className="text-xs text-slate-500">
          Updated {formatDateTime(article.updatedAt)}
          {article.wpPostId ? ` · WP #${article.wpPostId}` : ""}
        </p>
      </div>
      <StatusBadge status={article.status} />
    </Link>
  );
}
